import {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {
  Card,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Typography,
  Chip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {GET} from "APIclient";
import properties from "constants/ApplicationConstants";

const RATING_LEGEND = [
  {points: 1, rating: "Unsatisfactory", comment: "Performance is consistently non acceptable"},
  {
    points: 2,
    rating: "Below Expectations",
    comment: "Performance fails to meet job requirement on frequent basis"
  },
  {
    points: 3,
    rating: "Meets Expectations",
    comment: "Performance is regularly competent & dependable"
  },
  {
    points: 4,
    rating: "Exceeds Expectations",
    comment: "Performance is routinely above expectation"
  },
  {points: 5, rating: "Outstanding", comment: "Performance is consistently superior"}
];

const ROW_HEIGHT = 36;
const colStyle = {display: "flex", alignItems: "center", minHeight: ROW_HEIGHT};
const COL = {num: 0.4, skill: 2.2, empScore: 1.2, empComment: 3.4, mgrScore: 1.2, mgrComment: 3.6};

const scoreSx = {
  height: ROW_HEIGHT,
  fontSize: "0.8rem",
  fontFamily: properties.fontFamily,
  "& .MuiSelect-select": {py: "6px", color: "#000 !important", fontFamily: properties.fontFamily},
  "&.Mui-disabled .MuiSelect-select": {
    color: "#000 !important",
    WebkitTextFillColor: "#000 !important"
  }
};

const commentSx = {
  "& .MuiOutlinedInput-root": {height: ROW_HEIGHT, alignItems: "center"},
  "& .MuiInputBase-input": {
    py: "6px",
    fontSize: "0.8rem",
    fontFamily: properties.fontFamily,
    color: "#000 !important",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#000 !important",
    color: "#000 !important"
  }
};

function TabPanel({children, value, index}) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box p={0.5}>{children}</Box>}
    </div>
  );
}

function AdminViewAppraisalForm() {
  const navigate = useNavigate();
  const {appraisalId, empId} = useParams();

  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [notification, setNotification] = useState({open: false, message: "", type: ""});
  const [employeeData, setEmployeeData] = useState(null);
  const [appraisalData, setAppraisalData] = useState(null);
  const [goalsData, setGoalsData] = useState(null);
  const [viewData, setViewData] = useState(null);

  useEffect(() => {
    Promise.all([fetchEmployeeDetails(appraisalId, empId), fetchGoals(appraisalId, empId)]);
  }, [appraisalId, empId]);

  const fetchEmployeeDetails = async (appraisalId, employeeId) => {
    try {
      setLoading(true);
      const response = await GET(`manager/${appraisalId}/employee/${employeeId}/view`);
      if (response.data?.data) {
        const data = response.data.data;
        const nameParts = (data.employeeName || "").split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
        const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : "";
        setEmployeeData({
          empId: data?.empId || "",
          firstName,
          middleName,
          lastName,
          gender: data.gender || "",
          dateOfJoining: data.dateOfJoining || "",
          totalExperience: data.totalExperience || ""
        });
        setViewData(data);
        setAppraisalData(prev => ({...prev, questionnaire: data.questionnaire || []}));
      } else if (response.data?.statusCode === 400) {
        showNotification(response.data.message, "error");
      }
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to load employee details", "error");
    }
  };

  const fetchGoals = async (appraisalId, employeeId) => {
    try {
      const response = await GET(`manager/${appraisalId}/employee/${employeeId}/goals`);
      if (response.data?.data) {
        setGoalsData(response.data.data);
      } else if (response.data?.statusCode === 400) {
        showNotification(response.data.message, "error");
      }
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to load goals", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (goalsData && viewData) mergeSkillsData();
  }, [goalsData, viewData]);

  const mergeSkillsData = () => {
    const mergeSkills = (goalsSkills, viewSkills) =>
      (goalsSkills || []).map((goalSkill, index) => {
        const viewSkill = (viewSkills || [])[index] || {};
        return {
          skillName: goalSkill.skillSet,
          paFormId: goalSkill.paFormId,
          empScore: viewSkill.empScore || "",
          empComment: viewSkill.empComment || "",
          appraiserScore: viewSkill.appraiserScore || "",
          appraiserComment: viewSkill.appraiserComment || ""
        };
      });

    const transformedSkills = {
      technicalPrimarySkills: mergeSkills(
        goalsData?.technicalPrimary,
        viewData?.technicalSkills?.primary
      ),
      technicalSecondarySkills: mergeSkills(
        goalsData?.technicalSecondary,
        viewData?.technicalSkills?.secondary
      ),
      competencyPrimarySkills: mergeSkills(
        goalsData?.competencyPrimary,
        viewData?.competencySkills?.primary
      ),
      competencySecondarySkills: mergeSkills(
        goalsData?.competencySecondary,
        viewData?.competencySkills?.secondary
      )
    };

    setAppraisalData(prev => ({...prev, ...transformedSkills}));
  };

  const showNotification = (message, type = "info") => {
    setNotification({open: true, message, type});
    setTimeout(() => setNotification({open: false, message: "", type: ""}), 4000);
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  // ─── Info Field ───────────────────────────────────────────────────────────
  const InfoField = ({label, value, xs = 12, sm = 6, md = 3}) => (
    <Grid item xs={xs} sm={sm} md={md}>
      <MDBox
        p={0.5}
        sx={{
          backgroundColor: "#ffffff",
          borderRadius: 1,
          border: "1px solid #e0e0e0",
          minHeight: "38px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center"
        }}
      >
        <Typography
          variant="caption"
          fontWeight="600"
          color="text.secondary"
          sx={{
            mb: 0.1,
            textTransform: "uppercase",
            fontSize: "0.6rem",
            fontFamily: properties.fontFamily
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          color="text.primary"
          fontWeight={500}
          sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
        >
          {value || "-"}
        </Typography>
      </MDBox>
    </Grid>
  );

  // ─── Skills Table ─────────────────────────────────────────────────────────
  const renderSkillsTable = (skills, title) => {
    if (!skills?.length) {
      return (
        <Alert severity="warning" sx={{my: 0.5}}>
          <MDTypography variant="body2" sx={{fontFamily: properties.fontFamily}}>
            No {title} Added.
          </MDTypography>
        </Alert>
      );
    }

    const headerCellSx = {
      fontSize: "0.72rem",
      fontWeight: 700,
      color: "#444",
      lineHeight: 1.2,
      fontFamily: properties.fontFamily
    };

    return (
      <Box sx={{border: "1px solid #ddd", borderRadius: 1, overflow: "hidden", mb: 1}}>
        {/* Header */}
        <Grid container sx={{backgroundColor: "#f0f4f8", px: 0.75, py: 0.5}} alignItems="center">
          <Grid item xs={COL.num} sx={colStyle}>
            <Typography sx={headerCellSx}>#</Typography>
          </Grid>
          <Grid item xs={COL.skill} sx={colStyle}>
            <Typography sx={headerCellSx}>Skill Set</Typography>
          </Grid>
          <Grid item xs={COL.empScore} sx={{...colStyle, justifyContent: "center"}}>
            <Typography sx={{...headerCellSx, textAlign: "center"}}>Emp. Score</Typography>
          </Grid>
          <Grid item xs={COL.empComment} sx={colStyle}>
            <Typography sx={headerCellSx}>Emp. Comments</Typography>
          </Grid>
          <Grid item xs={COL.mgrScore} sx={{...colStyle, justifyContent: "center"}}>
            <Typography sx={{...headerCellSx, textAlign: "center"}}>Mgr Score</Typography>
          </Grid>
          <Grid item xs={COL.mgrComment} sx={colStyle}>
            <Typography sx={headerCellSx}>Manager Comment</Typography>
          </Grid>
        </Grid>

        {/* Rows */}
        {skills.map((skill, index) => (
          <Grid
            container
            key={index}
            alignItems="center"
            sx={{
              px: 0.75,
              py: 0.25,
              backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
              borderTop: "1px solid #eee",
              "&:hover": {backgroundColor: "#f5f9ff"},
              minHeight: ROW_HEIGHT
            }}
          >
            <Grid item xs={COL.num} sx={colStyle}>
              <Typography
                variant="body2"
                sx={{fontSize: "0.78rem", color: "#666", fontFamily: properties.fontFamily}}
              >
                {index + 1}
              </Typography>
            </Grid>

            <Grid item xs={COL.skill} sx={colStyle}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.8rem",
                  wordBreak: "break-word",
                  lineHeight: 1.3,
                  fontFamily: properties.fontFamily
                }}
              >
                {skill.skillName}
              </Typography>
            </Grid>

            {/* Employee Score */}
            <Grid item xs={COL.empScore} sx={{...colStyle, justifyContent: "center"}}>
              <FormControl size="small" disabled sx={{width: 68}}>
                <Select value={skill.empScore || ""} displayEmpty sx={scoreSx}>
                  <MenuItem value="" sx={{fontSize: "0.8rem", fontFamily: properties.fontFamily}}>
                    -
                  </MenuItem>
                  {[1, 2, 3, 4, 5].map(s => (
                    <MenuItem
                      key={s}
                      value={s}
                      sx={{fontSize: "0.8rem", fontFamily: properties.fontFamily}}
                    >
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Employee Comment */}
            <Grid item xs={COL.empComment} sx={colStyle}>
              <TextField
                fullWidth
                value={skill.empComment || ""}
                disabled
                size="small"
                variant="outlined"
                placeholder="-"
                sx={commentSx}
              />
            </Grid>

            {/* Manager Score */}
            <Grid item xs={COL.mgrScore} sx={{...colStyle, justifyContent: "center"}}>
              <FormControl size="small" disabled sx={{width: 68}}>
                <Select value={skill.appraiserScore || ""} displayEmpty sx={scoreSx}>
                  <MenuItem value="" sx={{fontSize: "0.8rem", fontFamily: properties.fontFamily}}>
                    -
                  </MenuItem>
                  {[1, 2, 3, 4, 5].map(s => (
                    <MenuItem
                      key={s}
                      value={s}
                      sx={{fontSize: "0.8rem", fontFamily: properties.fontFamily}}
                    >
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Manager Comment */}
            <Grid item xs={COL.mgrComment} sx={colStyle}>
              <TextField
                fullWidth
                value={skill.appraiserComment || ""}
                disabled
                size="small"
                variant="outlined"
                placeholder="-"
                sx={commentSx}
              />
            </Grid>
          </Grid>
        ))}
      </Box>
    );
  };

  // ─── Questionnaire ────────────────────────────────────────────────────────
  const renderQuestionnaire = questionnaire => {
    if (!questionnaire?.length) {
      return (
        <Alert severity="warning">
          <MDTypography variant="body2" sx={{fontFamily: properties.fontFamily}}>
            No Questionnaire Added.
          </MDTypography>
        </Alert>
      );
    }
    return (
      <MDBox>
        {questionnaire.map((item, index) => (
          <MDBox key={index} mb={0.75}>
            <MDTypography
              variant="body2"
              mb={0.25}
              color="dark"
              fontWeight="medium"
              sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
            >
              {item.question}
            </MDTypography>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={item.description || ""}
              disabled
              variant="outlined"
              size="small"
              placeholder="No answer provided"
              sx={{
                "& .MuiInputBase-input": {
                  color: "#000 !important",
                  fontSize: "0.82rem",
                  fontFamily: properties.fontFamily
                },
                "& .MuiInputBase-input.Mui-disabled": {WebkitTextFillColor: "#000 !important"}
              }}
            />
          </MDBox>
        ))}
      </MDBox>
    );
  };

  // ─── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  // ─── Main Render ──────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={0.5} pb={1.5}>
        <Card>
          {/* Header */}
          <MDBox
            variant="gradient"
            sx={{background: properties.colors.bgcolorbluethem}}
            borderRadius="lg"
            coloredShadow="success"
            mx={1.5}
            mt={0.75}
            p={0.75}
            textAlign="center"
          >
            <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
              <MDTypography
                variant="h5"
                fontWeight="medium"
                color="white"
                sx={{fontSize: "1rem", fontFamily: properties.fontFamily}}
              >
                Employee Appraisal Form
              </MDTypography>
              <Chip
                label="Admin View Only"
                size="small"
                sx={{
                  backgroundColor: "rgba(255,255,255,0.25)",
                  color: "white",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  fontFamily: properties.fontFamily,
                  border: "1px solid rgba(255,255,255,0.5)"
                }}
              />
            </MDBox>
            <MDTypography
              variant="body2"
              color="white"
              opacity={0.85}
              sx={{fontSize: "0.75rem", fontFamily: properties.fontFamily}}
            >
              Read-only view of employee self-assessment and manager ratings
            </MDTypography>
          </MDBox>

          <MDBox p={0.75}>
            {/* Admin Info Banner */}
            <Alert
              severity="info"
              sx={{mb: 0.75, fontSize: "0.82rem", fontFamily: properties.fontFamily}}
            >
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
              >
                You are viewing this appraisal form as an Admin. No changes can be made from this
                view.
              </Typography>
            </Alert>

            {/* Employee Official Details */}
            <Card variant="outlined" sx={{mb: 0.75}}>
              <MDBox
                px={0.75}
                pt={0.5}
                pb={0.25}
                sx={{
                  background: "linear-gradient(135deg, #e2e2e2 0%, #ffffffc9 100%)",
                  borderBottom: "1px solid #e0e0e0"
                }}
              >
                <MDTypography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{fontSize: "0.85rem", fontFamily: properties.fontFamily}}
                >
                  Official Details
                </MDTypography>
              </MDBox>
              <MDBox px={0.75} py={0.5}>
                <Grid container spacing={0.5}>
                  <InfoField label="Employee ID" value={employeeData?.empId} />
                  <InfoField label="First Name" value={employeeData?.firstName} />
                  <InfoField label="Middle Name" value={employeeData?.middleName} />
                  <InfoField label="Last Name" value={employeeData?.lastName} />
                  <InfoField label="Gender" value={employeeData?.gender} />
                  <InfoField
                    label="Date of Joining"
                    value={
                      employeeData?.dateOfJoining
                        ? new Date(employeeData.dateOfJoining).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric"
                          })
                        : "-"
                    }
                  />
                  <InfoField
                    label="Total Experience (Years)"
                    value={employeeData?.totalExperience}
                  />
                </Grid>
              </MDBox>
            </Card>

            {/* Rating Legend */}
            <Card variant="outlined" sx={{mb: 0.75}}>
              <MDBox
                px={0.75}
                pt={0.5}
                pb={0.25}
                sx={{backgroundColor: "#f7f7f7", borderBottom: "1px solid #e0e0e0"}}
              >
                <MDTypography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{fontSize: "0.85rem", fontFamily: properties.fontFamily}}
                >
                  Performance Evaluation Form
                </MDTypography>
              </MDBox>
              <MDBox px={0.75} py={0.5}>
                <MDTypography
                  variant="body2"
                  mb={0.5}
                  color="black"
                  sx={{fontSize: "0.78rem", fontFamily: properties.fontFamily}}
                >
                  Use the following ratings for consistency.
                </MDTypography>

                {/* Legend Header */}
                <Grid
                  container
                  sx={{
                    backgroundColor: "#f0f4f8",
                    px: 0.5,
                    py: 0.3,
                    borderRadius: "4px 4px 0 0",
                    border: "1px solid #ddd"
                  }}
                >
                  <Grid item xs={1}>
                    <Typography
                      sx={{fontSize: "0.72rem", fontWeight: 700, fontFamily: properties.fontFamily}}
                    >
                      Points
                    </Typography>
                  </Grid>
                  <Grid item xs={2.5}>
                    <Typography
                      sx={{fontSize: "0.72rem", fontWeight: 700, fontFamily: properties.fontFamily}}
                    >
                      Ratings
                    </Typography>
                  </Grid>
                  <Grid item xs={8.5}>
                    <Typography
                      sx={{fontSize: "0.72rem", fontWeight: 700, fontFamily: properties.fontFamily}}
                    >
                      Comments
                    </Typography>
                  </Grid>
                </Grid>

                {RATING_LEGEND.map((row, index) => (
                  <Grid
                    container
                    key={row.points}
                    sx={{
                      px: 0.5,
                      py: 0.2,
                      backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                      border: "1px solid #ddd",
                      borderTop: "none",
                      borderRadius: index === RATING_LEGEND.length - 1 ? "0 0 4px 4px" : 0
                    }}
                    alignItems="center"
                  >
                    <Grid item xs={1}>
                      <Typography sx={{fontSize: "0.78rem", fontFamily: properties.fontFamily}}>
                        {row.points}
                      </Typography>
                    </Grid>
                    <Grid item xs={2.5}>
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "#222",
                          fontFamily: properties.fontFamily
                        }}
                      >
                        {row.rating}
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <Typography
                        sx={{fontSize: "0.78rem", color: "#333", fontFamily: properties.fontFamily}}
                      >
                        {row.comment}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </MDBox>
            </Card>

            {/* Tabs */}
            <Card variant="outlined" sx={{mb: 0.75}}>
              <Box sx={{borderBottom: "1px solid #ddd"}}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  textColor="info"
                  indicatorColor="info"
                  variant="fullWidth"
                  sx={{
                    minHeight: 38,
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "0.82rem",
                      fontFamily: properties.fontFamily,
                      minHeight: 38,
                      py: 0
                    },
                    "& .Mui-selected": {color: "#2196f3 !important"}
                  }}
                >
                  <Tab label="Technical Skills" />
                  <Tab label="Performance Competency" />
                  <Tab label="Questionnaire" />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                <MDTypography
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={0.25}
                  sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
                >
                  Technical Primary Skills
                </MDTypography>
                {renderSkillsTable(
                  appraisalData?.technicalPrimarySkills,
                  "Technical Primary Skills"
                )}

                <MDTypography
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={0.25}
                  sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
                >
                  Technical Secondary Skills
                </MDTypography>
                {renderSkillsTable(
                  appraisalData?.technicalSecondarySkills,
                  "Technical Secondary Skills"
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <MDTypography
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={0.25}
                  sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
                >
                  Competency Primary Skills
                </MDTypography>
                {renderSkillsTable(
                  appraisalData?.competencyPrimarySkills,
                  "Competency Primary Skills"
                )}

                <MDTypography
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={0.25}
                  sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
                >
                  Competency Secondary Skills
                </MDTypography>
                {renderSkillsTable(
                  appraisalData?.competencySecondarySkills,
                  "Competency Secondary Skills"
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <MDTypography
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={0.5}
                  sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
                >
                  Questionnaire
                </MDTypography>
                {renderQuestionnaire(appraisalData?.questionnaire)}
              </TabPanel>
            </Card>

            {/* Back Button */}
            <MDBox display="flex" justifyContent="flex-start" mt={0.5}>
              <MDButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => navigate(-1)}
                sx={{fontFamily: properties.fontFamily}}
              >
                ← Back
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>

        {/* Notification */}
        {notification.open && (
          <MDBox sx={{position: "fixed", top: 16, right: 16, zIndex: 9999, minWidth: 280}}>
            <Alert
              severity={notification.type}
              onClose={() => setNotification({open: false, message: "", type: ""})}
              sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
            >
              {notification.message}
            </Alert>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default AdminViewAppraisalForm;
